import { useEffect, useRef, useState } from "react";
import { http, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Heart, Flame, Trophy, TrendingUp, Sparkles, Loader2, ImagePlus, X, Send, MessageSquare } from "lucide-react";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Textarea } from "@/components/ui/textarea";
import ShareMenu from "@/components/ShareMenu";

const ICONS = { rally: Flame, friend: TrendingUp, achievement: Trophy, training: Sparkles, post: MessageSquare };

export default function Social() {
  const { user } = useAuth();
  const [items, setItems] = useState(null);
  const [content, setContent] = useState("");
  const [photo, setPhoto] = useState("");
  const [posting, setPosting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => http.get("/social/highlights").then((r) => setItems(r.data)).catch((e) => toast.error(formatError(e)));
  useEffect(() => { load(); }, []);

  const react = async (h) => {
    try {
      const { data } = await http.post(`/social/highlights/${h.highlight_id}/react`);
      setItems((its) => its.map((x) => (x.highlight_id === h.highlight_id ? { ...x, reacted: data.reacted, reactions: data.reactions } : x)));
    } catch (e) { toast.error(formatError(e)); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) return toast.error("Image must be under 5MB.");
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await http.post("/social/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setPhoto(data.url);
    } catch (err) { toast.error(formatError(err)); }
    finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };

  const submitPost = async () => {
    if (!content.trim()) return toast.error("Write something first.");
    setPosting(true);
    try {
      const { data } = await http.post("/social/posts", { content: content.trim(), photo_url: photo || null });
      setItems((its) => [data, ...(its || [])]);
      setContent(""); setPhoto("");
      toast.success("Posted to the PaddleVerse!");
    } catch (e) { toast.error(formatError(e)); }
    finally { setPosting(false); }
  };

  if (!items) return <div className="grid place-items-center py-24"><Loader2 className="animate-spin pv-text-dark" /></div>;

  return (
    <div className="max-w-2xl mx-auto space-y-5" data-testid="social-page">
      <div>
        <h1 className="text-3xl font-black font-display pv-text-dark">From The PaddleVerse</h1>
        <p className="text-sm text-[#5C7285]">Share your wins and see what your local community is up to.</p>
      </div>

      {/* Composer */}
      <div className="rounded-2xl bg-white border border-[#0E395F]/10 p-4" data-testid="post-composer">
        <div className="flex gap-3">
          <Avatar className="h-10 w-10 border-2 pv-border-primary shrink-0">
            <AvatarImage src={user?.avatar} alt={user?.name} />
            <AvatarFallback>{user?.name?.[0] || "P"}</AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <Textarea data-testid="post-content-input" rows={2} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Share a win, a rally, or find a game…" className="resize-none" />
            {photo && (
              <div className="relative mt-3 rounded-xl overflow-hidden border border-black/10" data-testid="post-photo-preview">
                <img src={photo} alt="Post" className="w-full max-h-72 object-cover" />
                <button onClick={() => setPhoto("")} data-testid="post-photo-remove" className="absolute top-2 right-2 h-8 w-8 grid place-items-center rounded-full bg-black/60 text-white hover:bg-black/80 transition-colors"><X size={16} /></button>
              </div>
            )}
            <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/gif,image/webp" className="hidden" data-testid="post-photo-input" onChange={uploadPhoto} />
            <div className="flex items-center justify-between mt-3">
              <button data-testid="post-photo-trigger" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#4A90E2] hover:opacity-80 transition-opacity disabled:opacity-50">
                {uploading ? <Loader2 size={16} className="animate-spin" /> : <ImagePlus size={16} />} Photo
              </button>
              <button data-testid="post-submit-btn" onClick={submitPost} disabled={posting || !content.trim()} className="inline-flex items-center gap-2 bg-[#BED445] text-black font-extrabold rounded-full px-5 py-2 text-sm hover:bg-[#c9df55] transition-colors disabled:opacity-50">
                {posting ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} Post
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Feed */}
      {items.map((h) => {
        const Icon = ICONS[h.type] || Sparkles;
        return (
          <div key={h.highlight_id} data-testid="social-highlight-item" className="rounded-2xl bg-white border border-[#0E395F]/10 overflow-hidden">
            {h.is_post ? (
              <div className="flex items-center gap-3 px-4 pt-4">
                <Avatar className="h-9 w-9 border pv-border-primary"><AvatarImage src={h.author_avatar} alt={h.author_name} /><AvatarFallback>{h.author_name?.[0] || "P"}</AvatarFallback></Avatar>
                <div><div className="font-bold pv-text-dark text-sm">{h.author_name}</div><div className="text-[11px] text-[#5C7285]">Player post</div></div>
              </div>
            ) : null}
            {h.thumbnail && <div className={`bg-cover bg-center ${h.is_post ? "mt-3 h-56" : "h-44"}`} style={{ backgroundImage: `url(${h.thumbnail})` }} />}
            <div className="p-4">
              {!h.is_post && <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-wider text-[#F5B700]"><Icon size={12} />{h.type}</span>}
              {!h.is_post && <div className="font-bold text-lg pv-text-dark mt-1">{h.title}</div>}
              <div className={`text-[#5C7285] ${h.is_post ? "text-[15px] pv-text-dark" : "text-sm"}`}>{h.subtitle}</div>
              <div className="flex items-center gap-4 mt-3">
                <button data-testid="react-btn" onClick={() => react(h)} className={`inline-flex items-center gap-1.5 text-sm font-bold transition-colors ${h.reacted ? "text-[#F5B700]" : "text-[#5C7285] hover:text-[#0E395F]"}`}>
                  <Heart size={16} fill={h.reacted ? "currentColor" : "none"} />{h.reactions}
                </button>
                <ShareMenu item={h} />
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
